import { ActionTree } from 'vuex';
import { AuthState, CallbackToken, Company } from './types';
import { RootState } from '../types';
import { CompanyFactory } from '../../shared/factory/company/company.factory';
import { AuthenticationService } from '../../modules/authentication/services/authentication.service';
import { UsersFactory } from '../../shared/factory/users/users.factory';
import { Group } from '../../shared/model/user-model';

const decodedTokenFactory = (group: Group): any => {
    const parsedToken = AuthenticationService.parsedToken as any;
    // console.log('>>> token keycloak', parsedToken);

    let companies = [];
    let isB2B = false;

    if (group.name && group.name.indexOf('company_') > -1) {
        companies = group.attributes.companies;
    }

    if (group.attributes) {
        isB2B = group.attributes.sales_channel && group.attributes.sales_channel.indexOf('B2B') > -1;
    }

    const adapter: any = {
        'user_name': parsedToken.email,
        'scope': parsedToken.azp,
        'name': parsedToken.given_name,
        'exp': parsedToken.exp,
        'authorities': [
            {
                'companies': companies,
                'roles': parsedToken.authorities
            }
        ],
        'jti': parsedToken.jti,
        'client_id': parsedToken.azp,
        'isB2B': isB2B,
        'group': group,
        'sub': parsedToken.sub,
    };

    // console.log('>>> new decoded', adapter);
    return adapter;
};

export const actions: ActionTree<AuthState, RootState> = {
    login({ commit, state }, payload: CallbackToken): Promise<void> {
        return new Promise((resolve, reject) => {
            try {
                commit('logout');

                commit('ui/appVisibility', true, {root: true});
                commit('login', payload.access_token);

                new UsersFactory()
                .getGroupByUserLogged()
                .then((group) => {
                    // console.log('>>> api userGroupByUserLoggerd', group);

                    const decoded = decodedTokenFactory(group);

                    commit('updateToken', decoded);
                    let company = (decoded['authorities'] && decoded['authorities'].length > 0) ? decoded['authorities'][0].companies[0] : null;

                    if (localStorage.getItem('userLogged') !== decoded['sub']) {
                        localStorage.removeItem('companies');
                        localStorage.removeItem('workspace');
                    }

                    localStorage.setItem('userLogged', decoded['sub']);

                    if (!localStorage.getItem('companies')) {
                        new CompanyFactory().getAll(decoded['authorities'][0].companies).then(companies => {
                            localStorage.setItem('companies', JSON.stringify([...companies]));
                            commit('updateCompanies', companies);
                            if (!localStorage.getItem('workspace') && company) {
                                if (company === '*') {
                                    company = companies[0].companyKey;
                                }
                                localStorage.setItem('workspace', company);
                                commit('switchWorkspace', company);
                            } else if (localStorage.getItem('workspace') && !state.activeCompany) {
                                commit('switchWorkspace', localStorage.getItem('workspace'));
                            }
                            commit('initAmplitude');
                            resolve();
                        });
                    }
                    else {
                        const companies: Array<Company> = JSON.parse(localStorage.getItem('companies'));
                        commit('updateCompanies', companies);
                        if (!localStorage.getItem('workspace') && company) {
                            if (company === '*') {
                                company = (companies[0] as Company).companyKey;
                            }
                            localStorage.setItem('workspace', company);
                            commit('switchWorkspace', company);
                        } else if (localStorage.getItem('workspace') && !state.activeCompany) {
                            commit('switchWorkspace', localStorage.getItem('workspace'));
                        }
                        commit('initAmplitude');
                        resolve();
                    }
                });

            } catch (e) {
                console.error('Error in login', e);
                AuthenticationService.logout(window.location.origin);
                localStorage.removeItem('companies');
                commit('logout');
                reject(e);
            }
        });
    },
    // hardLogout is set to true whenever the user voluntarily logged out of the system
    logout({ commit }, hardLogout?: boolean): any {
        AuthenticationService.logout(window.location.origin);
        hardLogout && localStorage.removeItem('workspace');
        localStorage.removeItem('companies');
        commit('ui/appVisibility', false, { root: true });
        commit('logout');
    },
    checkToken({commit, state}): Promise<boolean> {
        return new Promise((resolve, reject) => {
            try {

                new UsersFactory()
                .getGroupByUserLogged()
                .then((group) => {
                    // console.log('>>> api userGroupByUserLoggerd', group);

                    const token: string = localStorage.getItem(AuthenticationService.TOKEN_KEY);
                    const decoded: Record<string, any> = decodedTokenFactory(group);

                    let company = (decoded['authorities'] && decoded['authorities'].length > 0) ? decoded['authorities'][0].companies[0] : null;
                    if (AuthenticationService.expDate < new Date()) {
                        AuthenticationService.logout(window.location.origin);
                        commit('ui/appVisibility', false, {root: true});
                        commit('logout');
                        resolve(false);
                        return;
                    }
                    if (token !== state.accessToken) {
                        // AuthenticationService.logout(window.location.origin);
                        commit('login', token);
                        commit('updateToken', decoded);
                    }

                    if (localStorage.getItem('userLogged') !== decoded['sub'] || !localStorage.getItem('companies')) {
                        localStorage.removeItem('companies');
                        localStorage.removeItem('workspace');
                    }

                    localStorage.setItem('userLogged', decoded['sub']);

                    if (!localStorage.getItem('companies')) {
                        new CompanyFactory().getAll(decoded['authorities'][0].companies).then(companies => {
                            localStorage.setItem('companies', JSON.stringify([...companies]));
                            commit('updateCompanies', companies);

                            if (!localStorage.getItem('workspace') && company) {
                                if (company === '*')
                                    company = JSON.parse(localStorage.getItem('companies'))[0].companyKey;

                                localStorage.setItem('workspace', company);
                                commit('switchWorkspace', company);
                            } else if (localStorage.getItem('workspace') && !state.activeCompany) {
                                commit('switchWorkspace', localStorage.getItem('workspace'));
                            }

                            commit('initAmplitude');
                            commit('ui/appVisibility', true, {root: true});
                            resolve(true);
                        });
                    }
                    else {
                        if (localStorage.getItem('companies') && state.companies.length === 0) {
                            const companies: Array<Company> = JSON.parse(localStorage.getItem('companies'));
                            commit('updateCompanies', companies);
                        }

                        if (!localStorage.getItem('workspace') && company) {
                            if (company === '*')
                                company = JSON.parse(localStorage.getItem('companies'))[0].companyKey;

                            localStorage.setItem('workspace', company);
                            commit('switchWorkspace', company);
                        } else if (localStorage.getItem('workspace') && !state.activeCompany) {
                            commit('switchWorkspace', localStorage.getItem('workspace'));
                        }

                        commit('initAmplitude');
                        commit('ui/appVisibility', true, {root: true});
                        resolve(true);
                    }
                });
            } catch (e) {
                console.error('Error in checkToken', e);
                AuthenticationService.logout(window.location.origin);
                localStorage.removeItem('companies');
                commit('logout');
                reject(e);
            }
        });
    },
    switchWorkspace({commit, state}, company: string): Promise<string> {
        return new Promise((resolve, reject) => {
            try {
                if (state && state.authorities && state.authorities.find(i => i.companies.includes(company) || i.companies.includes('*'))) {
                    localStorage.setItem('workspace', company);
                    commit('switchWorkspace', company);
                    resolve(company);
                } else {
                    reject();
                }
            } catch (e) {
                reject();
            }
        });
    }
};
